import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import useAxios from 'axios-hooks';

import { useAuth } from 'hooks/use-auth';
import { Time } from 'utils';
import {
  Equipment,
  ProductionState,
  productionStatesToStrings,
  Production,
  parseProductions
} from 'models';
import MyBread from 'components/Breadcrumbs';
import {
  StateIndicator
} from 'components/Production';


export default function EquipmentsPage() {
  const auth = useAuth();

  const [equipments, setEquipments] = useState<Equipment[]>([]);
  const [productions, setProductions] = useState<Production[]>([]);

  const [{
    data: dataEquipments,
    loading: loadingEquipments,
    error: errorEquipments
  }, refetchEquipments] = useAxios({
    url: `/v0/equipments`,
    headers: auth.authorizationHeader,
    params: {
      factory_id: auth.factoryId
    }
  });

  const [{
    data: dataProductions,
    loading: loadingProductions,
    error: errorProductions
  }, refetchProductions] = useAxios({
    url: `/v0/factories/${auth.factoryId}/productions`,
    headers: auth.authorizationHeader,
    params: {
      states: productionStatesToStrings([ProductionState.Running, ProductionState.Done]).join(',')
    }
  });

  useEffect(() => {
    setEquipments(dataEquipments?.map((a: any) => ({
      companyId: a.company_id,
      factoryId: a.factory_id,
      id: a.id,
      name: a.name,
      nameKana: a.name_kana,
      abbreviation: a.abbreviation
    } as Equipment)) || []);
  }, [dataEquipments]);

  useEffect(() => {
    setProductions(parseProductions(dataProductions)?.sort((a, b) => (
      (b.state === ProductionState.Running ? 1 : -1) ||
      (b.runningTimeTo?.getTime() || 0) - (a.runningTimeTo?.getTime() || 0)
    )));
  }, [dataProductions]);

  function makeEquipmentsElements() {
    if (loadingEquipments) {
      return (<p>Loading...</p>);
    }
    if (errorEquipments) {
      return (<p>Error!</p>);
    }
    return (
      <TableContainer component={Paper}>
        <Table>
          <TableBody>
          {equipments.map((item: Equipment) => (
            <TableRow key={item.id}>
              <TableCell>
                <Link to={`${item.id}`}>
                  [{item.id}] {item.name}{item.abbreviation && ` (${item.abbreviation})`}
                </Link>
              </TableCell>
              <TableCell>
              </TableCell>
            </TableRow>
          ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  function makeProductionsElements() {
    if (loadingProductions) {
      return (<p>Loading...</p>);
    }
    if (errorProductions) {
      return (<p>Error!</p>);
    }
    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">状態</TableCell>
              <TableCell align="left">設備</TableCell>
              <TableCell align="left">製品</TableCell>
              <TableCell>開始日時</TableCell>
              <TableCell>終了日時</TableCell>
              <TableCell>稼働時間</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
          {productions?.map((p) => (
            <TableRow
              key={p.id}
              sx={{'&:last-child td, &:last-child th': {border: 0}}}
            >
              <TableCell align="center">
                <StateIndicator state={p.state} />
              </TableCell>
              <TableCell align="left">{p.equipmentId} {p.equipmentName}</TableCell>
              <TableCell align="left">{p.productName} {p.productModel}</TableCell>
              <TableCell>{p.runningTimeFrom?.toLocaleString()}</TableCell>
              <TableCell>{p.runningTimeTo?.toLocaleString()}</TableCell>
              <TableCell>
              {p.runningTimeFrom && p.runningTimeTo && ((`${
                Time.msecToHour(p.runningTimeTo.getTime()
                  - p.runningTimeFrom.getTime()).toFixed(1)
              }h`))}
              </TableCell>
            </TableRow>
          ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Box sx={{width: '100%'}}>
      <MyBread />
      <Box>
        <Typography>設備一覧</Typography>
        {makeEquipmentsElements()}
      </Box>
      <Box>
        <Typography>製作履歴</Typography>
        {makeProductionsElements()}
      </Box>
    </Box>
  );
}
